import React, {useEffect} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import Modal from 'shared/migration-components/modal';

import {ModalContent, Inputs} from './styles';
import renderInputs from '../../render-inputs';
import {BaseInputProps} from 'shared/migration-components/form';
import Button from 'shared/migration-components/button';
import {NestedKeys} from 'shared/types/key-manipulation';
import {TranslationKeyProps} from 'shared/util/translated-props';
import useFormInputs from 'shared/migration-components/form/util';
import useApiQuery from 'shared/hooks/use-api-query';
import Loading from 'shared/migration-components/loading';
import {UseMutationResult} from 'react-query';
import getNestedKey from 'shared/util/get-nested-key';

interface Props {
  title: string;
  opened: boolean;
  onClose: () => void;
  inputs: (string | (() => BaseInputProps))[];
  onSubmit: (data: any) => void;
  defaultValues?: any;
  referenceValues?: any;
  translationKey: TranslationKeyProps;
  detailsUrl?: string;
  mutation?: () => UseMutationResult;
  validationSchema?: any;
  disableWhenDefaultValue?: NestedKeys[];
}

const DynamicSectionModal: React.FC<Props> = ({
  title,
  opened,
  onClose,
  inputs,
  onSubmit,
  defaultValues,
  translationKey,
  detailsUrl,
  mutation,
  validationSchema,
  disableWhenDefaultValue,
  referenceValues,
}) => {
  const formContext = useForm({defaultValues, resolver: validationSchema});
  const {getInput} = useFormInputs();

  const {data, isLoading} = useApiQuery({
    url: `${detailsUrl}/${defaultValues.id}`,
    enabled: !!detailsUrl,
  });

  const createMutation = mutation ? mutation() : ({} as UseMutationResult);

  const renderInput = (input: NestedKeys<any> | (() => BaseInputProps)) => {
    const name = typeof input === 'string' ? input : input().name!;
    const disabled = !!(
      disableWhenDefaultValue &&
      disableWhenDefaultValue.includes(name) &&
      getNestedKey(referenceValues ?? {}, name)
    );

    if (typeof input !== 'function')
      return renderInputs(
        getInput({
          name: input,
          border: true,
          disabled,
        })(),
        translationKey,
      );

    return renderInputs(
      {
        ...input(),
        border: true,
        disabled,
      },
      input().translationKey ?? translationKey,
    );
  };

  const handleSubmit = async () => {
    if (!mutation) return onSubmit(formContext.getValues());

    await createMutation.mutateAsync(formContext.getValues());

    if (!createMutation.isError) onSubmit(formContext.getValues());
  };

  useEffect(() => {
    if (data) formContext.reset({...data});
  }, [data]);

  if (isLoading) return <Loading />;

  return (
    <FormProvider {...formContext}>
      <Modal titleBar={{title: title}} size={1200} {...{opened, onClose}}>
        <ModalContent>
          <Inputs>{inputs.map(renderInput)}</Inputs>
          <Button
            title="Salvar"
            onClick={formContext.handleSubmit(handleSubmit)}
            loading={createMutation.isLoading}
          />
        </ModalContent>
      </Modal>
    </FormProvider>
  );
};

export default DynamicSectionModal;
